import React, { useState } from 'react';
import { z } from 'zod';
import { AgentVersion, ParameterDescriptor } from '@/redux/internal-services/agentsApi';
import { cn } from '@/utils/cn';

// ── style constants ────────────────────────────────────────────────────────────

export const inputCls =
  'w-full rounded-lg border border-[#EDEDED] dark:border-white/10 bg-ui-input px-3 py-2 text-sm text-[#272727] dark:text-gray-100 placeholder:text-ui-text3 ' +
  'focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark ' +
  'disabled:cursor-not-allowed disabled:bg-ui-raised disabled:opacity-60';

export const labelCls = 'block text-sm font-medium text-[#272727] dark:text-gray-100 mb-1';

// ── helpers ────────────────────────────────────────────────────────────────────

export const uid = () => Math.random().toString(36).slice(2, 9);

export function relTime(iso?: string): string {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function fmtDuration(sec?: number): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

// ── version snapshot ───────────────────────────────────────────────────────────

export const SNAP_FIELDS = [
  'system_prompts', 'audio_prompt', 'models', 'model_parameters', 'tools', 'voice',
] as const;

export function snapshot(form: Record<string, unknown>): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  SNAP_FIELDS.forEach((k) => { o[k] = form[k]; });
  return JSON.parse(JSON.stringify(o));
}

export function makeVersion(form: Record<string, unknown>, message: string): AgentVersion {
  const versions = (form.versions as AgentVersion[] | undefined) || [];
  const n = versions.length ? Math.max(...versions.map((v) => v.n)) + 1 : 1;
  return {
    id: uid(), n,
    message: message || `Version ${n}`,
    created_at: new Date().toISOString(),
    snapshot: snapshot(form),
  };
}

export function promptText(prompts?: Array<{ title?: string; prompt: string }>): string {
  return (prompts || []).map((p) => (p.title ? `# ${p.title}\n` : '') + p.prompt).join('\n\n');
}

export function lineDiff(a: string, b: string) {
  const x = (a || '').split('\n'), y = (b || '').split('\n');
  const m = x.length, n = y.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: Array<{ t: 'same' | 'add' | 'del'; v: string }> = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (x[i] === y[j]) { out.push({ t: 'same', v: x[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: 'del', v: x[i] }); i++; }
    else { out.push({ t: 'add', v: y[j] }); j++; }
  }
  while (i < m) out.push({ t: 'del', v: x[i++] });
  while (j < n) out.push({ t: 'add', v: y[j++] });
  return out;
}

// ── constants ──────────────────────────────────────────────────────────────────

export interface PromptVariable {
  token: string;
  label: string;       // full label shown in the checkbox list
  short: string;       // short label shown on the Insert chips
  group: 'User' | 'Tenant';
}

export const PROMPT_VARIABLES: PromptVariable[] = [
  { token: 'user.name',             label: 'User name',            short: 'Name',          group: 'User' },
  { token: 'user.role',             label: 'User role',            short: 'Role',          group: 'User' },
  { token: 'user.timezone',         label: 'User timezone',        short: 'Timezone',      group: 'User' },
  { token: 'user.location',         label: 'User location',        short: 'Location',      group: 'User' },
  { token: 'tenant.name',           label: 'Company name',         short: 'Company name',  group: 'Tenant' },
  { token: 'tenant.company_email',  label: 'Company email',        short: 'Email',         group: 'Tenant' },
  { token: 'tenant.company_address',label: 'Company address',      short: 'Address',       group: 'Tenant' },
  { token: 'tenant.website',        label: 'Website',              short: 'Website',       group: 'Tenant' },
  { token: 'tenant.work_types',     label: 'Work types',           short: 'Work types',    group: 'Tenant' },
  { token: 'tenant.service_regions',label: 'Service regions',      short: 'Regions',       group: 'Tenant' },
];

// ── Zod schema + form type ─────────────────────────────────────────────────────

const systemPromptSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  prompt: z.string(),
  order: z.number().optional(),
  category: z.string().optional(),
  // roles that see this block; empty / undefined = always injected
  condition_roles: z.array(z.string()).optional(),
});

export type PromptCategory = 'persona' | 'rules' | 'context' | 'format' | 'tools' | string;

export const agentSchema = z.object({
  agent_name: z.string().min(1, 'Agent name is required'),
  description: z.string().optional(),
  agent_type: z.enum(['conversational', 'workflow']).optional(),
  trigger_type: z.enum(['manual', 'schedule', 'webhook', 'event']).optional(),
  status: z.string(),
  models: z.object({
    text: z.string().min(1, 'Model is required'),
    realtime: z.string().nullable().optional(),
  }),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  model_parameters: z.record(z.string(), z.unknown()).optional(),
  voice: z.record(z.string(), z.unknown()).optional(),
  // Free-form audio-mode prefix, prepended to the composed system prompt when the current turn
  // is voice (realtime + push-to-talk). Kept as one editable field so brevity / no-greeting /
  // no-filler rules live in one place per agent instead of being hardcoded in the backend.
  audio_prompt: z.string().optional(),
  system_prompts: z.array(systemPromptSchema).optional(),
  tools: z.array(z.any()).optional(),
  versions: z.array(z.any()).optional(),
  published_version_id: z.string().optional(),
  tenant_id: z.string().nullable().optional(),
  icon_url: z.string().optional(),
});

export type AgentFormValues = z.infer<typeof agentSchema>;

// ── prompt category config ─────────────────────────────────────────────────────

export const PROMPT_CATEGORIES: Record<PromptCategory, { label: string; chip: string; dot: string }> = {
  persona:  { label: 'Persona',  chip: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  rules:    { label: 'Rules',    chip: 'bg-red-50 text-red-700 border-red-200',          dot: 'bg-red-500' },
  context:  { label: 'Context',  chip: 'bg-blue-50 text-blue-700 border-blue-200',       dot: 'bg-blue-500' },
  format:   { label: 'Format',   chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  tools:    { label: 'Tools',    chip: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-500' },
};

export const TENANT_ROLES: { value: string; label: string }[] = [
  { value: 'tenant_admin',     label: 'Admin' },
  { value: 'sales_agent',      label: 'Sales' },
  { value: 'project_manager',  label: 'Project Manager' },
  { value: 'office_admin',     label: 'Office Admin' },
  { value: 'crew_member',      label: 'Crew' },
];

export const emptyAgent: AgentFormValues = {
  agent_name: '',
  description: '',
  models: { text: '', realtime: null },
  status: 'active',
  tenant_id: null,
  tools: [],
  audio_prompt: '',
  system_prompts: [],
  model_parameters: {},
  voice: {},
  versions: [],
  tags: [],
  category: '',
};

// ── SectionCard ────────────────────────────────────────────────────────────────

export const SectionCard: React.FC<{
  title?: string;
  desc?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}> = ({ title, desc, children, right, className }) => (
  <div className={cn('bg-ui-card border border-[#EDEDED] dark:border-white/10 rounded-xl shadow-sm mb-5', className)}>
    {(title || right) && (
      <div className="flex items-start justify-between px-5 pt-5 pb-0">
        <div>
          {title && <h2 className="text-sm font-semibold text-[#272727] dark:text-gray-100">{title}</h2>}
          {desc && <p className="text-xs text-ui-text3 mt-0.5">{desc}</p>}
        </div>
        {right}
      </div>
    )}
    <div className="px-5 py-4">{children}</div>
  </div>
);

// ── Toggle ─────────────────────────────────────────────────────────────────────

export const Toggle: React.FC<{ on: boolean; onChange: () => void; disabled?: boolean }> = ({ on, onChange, disabled }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={cn(
      'relative h-5 w-9 rounded-full transition-colors shrink-0',
      on ? 'bg-emerald-500' : 'bg-gray-300',
      disabled && 'opacity-50 cursor-not-allowed',
    )}
  >
    <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all', on ? 'left-[18px]' : 'left-0.5')} />
  </button>
);

// ── JsonField ──────────────────────────────────────────────────────────────────

export const JsonField: React.FC<{
  value: unknown;
  onChange: (v: unknown) => void;
  rows?: number;
  placeholder?: string;
}> = ({ value, onChange, rows = 4, placeholder }) => {
  const [text, setText] = useState<string>(() =>
    typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)
  );
  const [bad, setBad] = useState(false);
  return (
    <>
      <textarea
        className={cn(inputCls, 'font-mono', bad && 'border-[#CF4148] focus:ring-[#CF4148]')}
        rows={rows}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value || '{}')); setBad(false); }
          catch { setBad(true); }
        }}
      />
      {bad && <p className="mt-1 text-[11px] text-[#CF4148]">Invalid JSON — last valid value kept.</p>}
    </>
  );
};

// ── Modal ──────────────────────────────────────────────────────────────────────

export const Modal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}> = ({ title, onClose, children, footer, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
    <div
      onMouseDown={(e) => e.stopPropagation()}
      className={cn('w-full rounded-2xl border border-[#EDEDED] dark:border-white/10 bg-ui-card shadow-xl', wide ? 'max-w-2xl' : 'max-w-lg')}
    >
      <div className="flex items-center justify-between border-b border-[#EDEDED] dark:border-white/10 px-5 py-3.5">
        <h3 className="text-base font-semibold text-[#272727] dark:text-gray-100">{title}</h3>
        <button onClick={onClose} className="rounded-md p-1 text-ui-text3 hover:bg-gray-100 dark:hover:bg-white/10">✕</button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-[#EDEDED] dark:border-white/10 px-5 py-3">{footer}</div>}
    </div>
  </div>
);

// ── AgentTypeBadge ─────────────────────────────────────────────────────────────

export const AgentTypeBadge: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return null;
  const cls: Record<string, string> = {
    conversational: 'bg-purple-50 text-purple-600 border-purple-100',
    workflow: 'bg-orange-50 text-orange-600 border-orange-100',
  };
  const label: Record<string, string> = {
    conversational: 'Chat',
    workflow: 'Workflow',
  };
  return (
    <span className={cn('px-2 py-0.5 text-xs rounded-full border font-medium', cls[type] ?? 'bg-ui-subtle text-ui-text2 border-ui-border')}>
      {label[type] ?? type}
    </span>
  );
};

// ── IngestionDot ───────────────────────────────────────────────────────────────

export const IngestionDot: React.FC<{ status?: string }> = ({ status }) => {
  const dotCls: Record<string, string> = {
    pending: 'bg-gray-400',
    processing: 'bg-yellow-400 animate-pulse',
    completed: 'bg-emerald-500',
    failed: 'bg-red-500',
  };
  const label: Record<string, string> = {
    pending: 'Pending', processing: 'Processing…', completed: 'Indexed', failed: 'Failed',
  };
  if (!status) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-full shrink-0', dotCls[status] ?? 'bg-gray-300')} />
      <span className="text-xs text-ui-text2">{label[status] ?? status}</span>
    </span>
  );
};

// ── RunStatusPill ──────────────────────────────────────────────────────────────

import { AgentRun } from '@/redux/internal-services/agentsApi';

export const RunStatusPill: React.FC<{ status: AgentRun['status'] }> = ({ status }) => {
  const cls: Record<string, string> = {
    pending: 'bg-ui-subtle text-ui-text2 border-ui-border',
    running: 'bg-blue-50 text-blue-600 border-blue-100',
    completed: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    failed: 'bg-red-50 text-red-500 border-red-100',
    cancelled: 'bg-orange-50 text-orange-500 border-orange-100',
  };
  return (
    <span className={cn('px-2 py-0.5 text-xs rounded-full border', cls[status] ?? cls.pending)}>
      {status}
    </span>
  );
};

// ── DescriptorField ────────────────────────────────────────────────────────────

export const DescriptorField: React.FC<{
  d: ParameterDescriptor;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ d, value, onChange }) => {
  if (d.type === 'bool') {
    return (
      <label className="flex items-center gap-2 cursor-pointer py-1">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-brand-dark"
        />
        <span className="text-sm text-[#272727] dark:text-gray-100">{d.label}</span>
      </label>
    );
  }
  return (
    <div>
      <label className={labelCls}>{d.label}</label>
      {d.type === 'enum' && d.options ? (
        <select className={inputCls} value={String(value ?? d.default ?? '')} onChange={(e) => onChange(e.target.value)}>
          {d.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : d.type === 'list' ? (
        <input
          className={inputCls}
          value={Array.isArray(value) ? (value as string[]).join(', ') : ''}
          onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          placeholder="comma,separated"
        />
      ) : d.type === 'int' || d.type === 'float' ? (
        <input
          className={inputCls}
          type="number"
          min={d.min}
          max={d.max}
          step={d.type === 'float' ? 0.1 : 1}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      ) : (
        <input className={inputCls} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
};

// ── FeatureFlagsEditor ─────────────────────────────────────────────────────────

export const FeatureFlagsEditor: React.FC<{
  flags: Record<string, unknown>;
  onChange: (flags: Record<string, unknown>) => void;
}> = ({ flags, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const entries = Object.entries(flags);
  const update = (key: string, val: unknown) => onChange({ ...flags, [key]: val });
  const remove = (key: string) => {
    const next = { ...flags };
    delete next[key];
    onChange(next);
  };
  const addFlag = () => {
    const k = newKey.trim().replace(/\s+/g, '_');
    if (!k || k in flags) return;
    onChange({ ...flags, [k]: false });
    setNewKey('');
  };
  return (
    <div className="space-y-2">
      {entries.length === 0 && <p className="text-xs text-ui-text3">No feature flags defined.</p>}
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3 rounded-lg border border-[#EDEDED] dark:border-white/10 px-3 py-2">
          <code className="flex-1 text-xs font-mono text-[#272727] dark:text-gray-100">{k}</code>
          {typeof v === 'boolean' ? (
            <Toggle on={v} onChange={() => update(k, !v)} />
          ) : (
            <input className={cn(inputCls, 'w-32 text-xs')} value={String(v ?? '')} onChange={(e) => update(k, e.target.value)} />
          )}
          <button type="button" onClick={() => remove(k)} className="text-xs text-[#CF4148] hover:underline shrink-0">Remove</button>
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <input
          className={cn(inputCls, 'flex-1')}
          placeholder="flag_key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addFlag()}
        />
        <button
          type="button"
          onClick={addFlag}
          className="shrink-0 rounded-lg border border-brand-light px-3 py-1.5 text-sm text-brand-dark hover:bg-brand-light/50 transition"
        >
          Add flag
        </button>
      </div>
    </div>
  );
};
